import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BellRing,
  ImagePlus,
  Send,
  Trash2,
  X,
} from "lucide-react";
import api from "../../lib/Urls";
import {
  CLOUDINARY_PURPOSES,
  uploadDirectToCloudinary,
} from "../../lib/cloudinaryUpload";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const MAX_NOTICE_IMAGE_BYTES = 8 * 1024 * 1024;

const AdminNotices = () => {
  const navigate = useNavigate();
  const previewUrlRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    message: "",
    actionUrl: "",
    targetAudience: "ALL_USERS",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const clearPreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const resetImage = useCallback(() => {
    clearPreviewUrl();
    setImageFile(null);
    setImagePreview("");
    setUploadProgress(0);
  }, [clearPreviewUrl]);

  useEffect(() => {
    return () => clearPreviewUrl();
  }, [clearPreviewUrl]);

  const loadNotices = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/notices");
      setNotices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/", { replace: true });
        return;
      }

      setError("Unable to load notices.");
      console.error("Failed to load notices", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));

    setStatus("");
    setError("");
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }

    if (file.size > MAX_NOTICE_IMAGE_BYTES) {
      setError("Notice image must be 8 MB or smaller.");
      return;
    }

    clearPreviewUrl();

    const previewUrl = URL.createObjectURL(file);
    previewUrlRef.current = previewUrl;

    setImageFile(file);
    setImagePreview(previewUrl);
    setUploadProgress(0);
    setError("");
    setStatus("");
  };

  const publish = async (event) => {
    event.preventDefault();

    setPublishing(true);
    setStatus("");
    setError("");
    setUploadProgress(0);

    try {
      let uploadedImage = null;

      if (imageFile) {
        uploadedImage = await uploadDirectToCloudinary(
          imageFile,
          CLOUDINARY_PURPOSES.NOTICE_IMAGE,
          {
            onProgress: setUploadProgress,
          }
        );
      }

      const res = await api.post("/api/admin/notices", {
        ...form,
        actionUrl: form.actionUrl.trim() || null,
        imageUrl: uploadedImage?.secureUrl || null,
        imagePublicId: uploadedImage?.publicId || null,
      });

      setNotices((current) => [res.data, ...current]);

      setStatus(
        "Notice published as DealSwapify. In-app and push delivery is continuing in the background."
      );

      setForm({
        title: "",
        message: "",
        actionUrl: "",
        targetAudience: "ALL_USERS",
      });

      resetImage();
    } catch (err) {
      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/", { replace: true });
        return;
      }

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Unable to publish notice."
      );
    } finally {
      setPublishing(false);
    }
  };

  const deleteNotice = async (notice) => {
    if (!notice?.noticeId || deletingId) return;

    const confirmed = window.confirm(
      `Delete "${notice.title}"? This removes the notice and its stored in-app notifications.`
    );

    if (!confirmed) return;

    setDeletingId(notice.noticeId);
    setError("");
    setStatus("");

    try {
      await api.delete(
        `/api/admin/notices/${notice.noticeId}`
      );

      setNotices((current) =>
        current.filter(
          (item) => item.noticeId !== notice.noticeId
        )
      );

      setStatus("Notice deleted.");
    } catch (err) {
      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/", { replace: true });
        return;
      }

      setError(
        err.response?.data?.message ||
          "Unable to delete notice."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="container mx-auto flex-1 px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6 flex items-center gap-3">
            <Link
              to="/admin/users"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card hover:bg-muted"
              aria-label="Back to user management"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>

            <div>
              <div className="flex items-center gap-2">
                <BellRing className="h-5 w-5 text-primary" />

                <h1 className="font-heading text-2xl font-bold">
                  DealSwapify Notices
                </h1>
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                Publish official in-app and push notices as
                DealSwapify.
              </p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <form
              onSubmit={publish}
              className="space-y-4 rounded-xl border border-border bg-card p-5"
            >
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Posting identity
                </p>

                <p className="mt-1 font-heading text-lg font-bold text-primary">
                  DealSwapify
                </p>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Title
                </span>

                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  maxLength={160}
                  required
                  disabled={publishing}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                  placeholder="Marketplace update"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Notice
                </span>

                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  maxLength={2000}
                  required
                  disabled={publishing}
                  rows={6}
                  className="w-full resize-y rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                  placeholder="Write the official DealSwapify notice..."
                />
              </label>

              <div>
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Photo (optional)
                </span>

                {imagePreview ? (
                  <div className="relative overflow-hidden rounded-xl border border-border bg-secondary">
                    <img
                      src={imagePreview}
                      alt="Notice preview"
                      className="max-h-64 w-full object-cover"
                    />

                    <button
                      type="button"
                      onClick={resetImage}
                      disabled={publishing}
                      aria-label="Remove notice image"
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-secondary/50 px-4 py-6 text-sm text-muted-foreground transition hover:border-primary/50 hover:text-foreground">
                    <ImagePlus className="h-5 w-5" />
                    Add notice photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={publishing}
                      onChange={handleImageChange}
                    />
                  </label>
                )}

                {publishing &&
                  imageFile &&
                  uploadProgress > 0 &&
                  uploadProgress < 100 && (
                    <div className="mt-2">
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{
                            width: `${uploadProgress}%`,
                          }}
                        />
                      </div>

                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Uploading photo {uploadProgress}%
                      </p>
                    </div>
                  )}

                <p className="mt-1 text-[11px] text-muted-foreground">
                  Images up to 8 MB. The photo is optional.
                </p>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Audience
                </span>

                <select
                  name="targetAudience"
                  value={form.targetAudience}
                  onChange={handleChange}
                  disabled={publishing}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                >
                  <option value="ALL_USERS">
                    All registered users
                  </option>

                  <option value="SIGNED_IN_USERS">
                    Users with valid sign-in tokens
                  </option>
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Extra action path (optional)
                </span>

                <input
                  name="actionUrl"
                  value={form.actionUrl}
                  onChange={handleChange}
                  maxLength={1000}
                  disabled={publishing}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                  placeholder="/how-it-works"
                />

                <span className="mt-1 block text-[11px] text-muted-foreground">
                  Notification clicks always open the dedicated
                  notice page. This optional path appears there as
                  an additional action.
                </span>
              </label>

              {status && (
                <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
                  {status}
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={publishing}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
              >
                {publishing ? (
                  <div className="h-4 w-4 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}

                {publishing
                  ? "Publishing..."
                  : "Publish as DealSwapify"}
              </button>
            </form>

            <section className="rounded-xl border border-border bg-card p-5">
              <h2 className="font-semibold">
                Recent notices
              </h2>

              <p className="mt-1 text-xs text-muted-foreground">
                The real administrator remains available only in
                the backend audit log.
              </p>

              <div className="mt-4 space-y-3">
                {loading ? (
                  <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                    <div className="h-4 w-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                    Loading notices...
                  </div>
                ) : notices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No DealSwapify notices yet.
                  </p>
                ) : (
                  notices.map((notice) => (
                    <article
                      key={notice.noticeId}
                      className="overflow-hidden rounded-lg border border-border bg-secondary/40"
                    >
                      {notice.imageUrl && (
                        <Link
                          to={`/notices/${notice.noticeId}`}
                          className="block"
                        >
                          <img
                            src={notice.imageUrl}
                            alt=""
                            className="max-h-44 w-full object-cover"
                          />
                        </Link>
                      )}

                      <div className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <Link
                            to={`/notices/${notice.noticeId}`}
                            className="min-w-0 flex-1"
                          >
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                              DealSwapify
                            </p>

                            <h3 className="mt-1 font-semibold hover:text-primary">
                              {notice.title}
                            </h3>
                          </Link>

                          <button
                            type="button"
                            onClick={() =>
                              deleteNotice(notice)
                            }
                            disabled={
                              deletingId === notice.noticeId
                            }
                            aria-label={`Delete ${notice.title}`}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-destructive/30 text-destructive transition hover:bg-destructive/10 disabled:opacity-50"
                          >
                            {deletingId ===
                            notice.noticeId ? (
                              <div className="h-4 w-4 rounded-full border-2 border-destructive/30 border-t-destructive animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>

                        <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm text-muted-foreground">
                          {notice.message}
                        </p>

                        <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                          <span>
                            {notice.targetAudience ===
                            "SIGNED_IN_USERS"
                              ? "Valid-token users"
                              : "All users"}
                          </span>

                          <span>•</span>

                          <span>
                            {notice.deliveredCount} in-app
                            delivered
                          </span>

                          <span>•</span>

                          <span>
                            {new Date(
                              notice.createdAt
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminNotices;
